package co.uk.paxes.pinpoint.model

import kotlin.random.Random

/**
 * Easy difficulty AI strategy
 * - Random move selection
 * - 20% chance of suboptimal moves
 * - No joker usage
 */
class EasyStrategy(private val playerId: String) : AIStrategy {
    
    override fun selectMove(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Move? {
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        if (emptyPositions.isEmpty() || availablePins.isEmpty()) {
            return null
        }
        
        val currentPlayer = gameState.currentPlayer
        val isLateGame = emptyPositions.size < 40
        val jokerChance = if (isLateGame) 0.15f else 0.05f
        
        if (currentPlayer.canUseJoker() && Random.nextFloat() < jokerChance) {
            val sacrificePin = availablePins.random()
            val validJokerColors = PinColor.values().filter { it != PinColor.BLACK && it != sacrificePin.color }
            if (validJokerColors.isNotEmpty()) {
                val jokerColor = validJokerColors.random()
                val jokerPin = Pin.joker(sacrificePin.color, jokerColor)
                val randomPosition = emptyPositions.random()
                return Move(playerId, jokerPin, randomPosition)
            }
        }
        
        // 20% chance to make a suboptimal move (pick from worst 30% of positions)
        val shouldMakeSuboptimal = Random.nextFloat() < 0.2f
        
        if (shouldMakeSuboptimal && emptyPositions.size > 3) {
            // Pick from less desirable positions (edges/corners)
            val suboptimalPositions = emptyPositions.sortedBy { position ->
                val (row, col) = position.toLegacy()
                // Prefer edges and corners for suboptimal play
                -(kotlin.math.abs(row - 4.5) + kotlin.math.abs(col - 4.5))
            }.take(emptyPositions.size / 3)
            
            val randomPin = availablePins.random()
            val randomPosition = suboptimalPositions.random()
            return Move(playerId, randomPin, randomPosition)
        }
        
        // Normal random move
        val randomPin = availablePins.random()
        val randomPosition = emptyPositions.random()
        return Move(playerId, randomPin, randomPosition)
    }
    
    override fun shouldCreateJoker(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Boolean {
        // AI Easy uses jokers randomly
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        val isLateGame = emptyPositions.size < 40
        val jokerChance = if (isLateGame) 0.15f else 0.05f
        return Random.nextFloat() < jokerChance
    }
    
    override fun selectJokerColor(
        gameState: GameState,
        boardAnalyzer: BoardAnalyzer
    ): PinColor? {
        // AI Easy picks a random valid color
        val validColors = PinColor.values().filter { it != PinColor.BLACK }
        return validColors.randomOrNull()
    }
}